import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { ReauthModalComponent } from 'app/components/modal/reauthModal.component';
import { AuthServiceErrorHandler } from 'app/services/authServiceErrorHandler';
import { Role, RoleRepository } from 'app/services/database/role.repository';
import { UserRepository } from 'app/services/database/user.repository';
import { ModalService } from 'app/services/modal.service';
import { UserModel } from 'app/views/user/models/user.model';
import { FirebaseError, User } from 'firebase/app';
import * as firebase from 'firebase/app';

export class EmailNotVerifiedError implements FirebaseError {
    public get name() { return 'auth/email-not-verified'; }
    public get code() { return `auth/email-not-verified`; }
    public get message() { return `The user's email is not verified.`; }
}

export enum LoginResult { Connected, EmailNotVerified, WrongCredentials }

@Injectable()
export class AuthService {
    constructor(
        private afAuth: AngularFireAuth,
        private errorHandler: AuthServiceErrorHandler,
        private modalService: ModalService,
        private userRepository: UserRepository,
        private roleRepository: RoleRepository
    ) { }

    public async isVerified(): Promise<boolean> {
        let user = await this.getCurrentUser();
        return user && user.emailVerified;
    }

    public async getCurrentUser() {
        return new Promise<User>((resolve, reject) => {
            this.afAuth.authState.subscribe(
                user => resolve(user),
                err => this.errorHandler.handleError(err)
            );
        });
    }

    public async isAuthenticated(): Promise<boolean> {
        let user = await this.getCurrentUser();
        return !!user;
    }

    public async authenticateWithModal() {
        return await this.modalService.open(ReauthModalComponent);
    }

    public async createUser(email: string, password: string, userInfo: UserModel): Promise<boolean> {
        return await this.tryAction(async () => {
            let credentials = await this.afAuth.auth.createUserWithEmailAndPassword(email, password);
            await credentials.user.sendEmailVerification();
            await this.roleRepository.set(credentials.user.uid, Role.User);

            userInfo.owners[credentials.user.uid] = true;
            await this.userRepository.set(credentials.user.uid, userInfo);
        });
    }

    public async logIn(email: string, password: string): Promise<LoginResult> {
        let result = LoginResult.Connected;

        try {
            let credentials = await this.afAuth.auth.signInWithEmailAndPassword(email, password);
            if (!credentials.user.emailVerified) {
                result = LoginResult.EmailNotVerified;
            }
        } catch (err) {
            await this.errorHandler.handleError(err);
            result = LoginResult.WrongCredentials;
        }

        return result;
    }

    public async logOut() {
        await this.afAuth.auth.signOut();
    }

    public async sendPasswordResetEmail(email: string) {
        try {
            await this.afAuth.auth.sendPasswordResetEmail(email);
            return true;
        } catch (err) {
            if (this.errorHandler.convertError(err).name === 'UserNotFoundError') return true;
            this.errorHandler.handleError(err);
        }

        return false;
    }

    public async sendEmailVerification() {
        let user = await this.getCurrentUser();
        if (!user) return false;

        return await this.tryAction(async () => await user.sendEmailVerification());
    }

    public async updateEmail(newEmail: string) {
        let user = await this.getCurrentUser();
        if (!user) return false;

        return await this.tryWithReauthentication(async () => {
            await user.updateEmail(newEmail);
            await user.sendEmailVerification();
        });
    }

    public async reauthenticate(password: string) {
        let user = await this.getCurrentUser();
        if (!user) return;

        return await this.tryAction(async () => {
            let credential = firebase.auth.EmailAuthProvider.credential(user.email, password);
            await user.reauthenticateWithCredential(credential);
        });
    }

    public async verifyEmail(actionCode: string) {
        return await this.tryAction(async () => await this.afAuth.auth.applyActionCode(actionCode));
    }

    public async changePassword(actionCode: string, newPassword: string) {
        return await this.tryAction(async () => await this.afAuth.auth.confirmPasswordReset(actionCode, newPassword));
    }

    public async getIdToken() {
        try {
            let user = await this.getCurrentUser();
            if (!user) return undefined;
            return await user.getIdToken();
        } catch (err) {
            return undefined;
        }
    }

    private async tryAction(action: (() => void)) {
        try {
            await action();
            return true;
        } catch (err) {
            this.errorHandler.handleError(err);
        }

        return false;
    }

    private async tryWithReauthentication(action: (() => void)) {
        try {
            await action();
        } catch (err) {
            if (this.errorHandler.convertError(err).name !== 'RecentLoginRequiredError') {
                await this.errorHandler.handleError(err);
                return false;
            }

            try {
                if (!await this.authenticateWithModal()) return false;
                await action();
            } catch (err) {
                await this.errorHandler.handleError(err);
                return false;
            }
        }

        return true;
    }
}



// WEBPACK FOOTER //
// ./src/app/services/auth.service.ts